import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { HiChevronDown, HiArrowRight } from "react-icons/hi";
import { Link } from "react-router-dom";

import FadeIn from "../components/Animation/FadeIn";
import IFHeading from "../components/IF_Heading";
import IFSwiper from "../components/IF_Swiper";
import IFSubMenu from "../components/IF_SubMenu";
import { getHome, getHomeCache } from "../services";

function SafeJsonParse(str, fallback = []) {
    try {
        return JSON.parse(str);
    } catch {
        return fallback;
    }
}

export default function Home() {
    const cached = getHomeCache();
    const [data, setDatas] = useState(() => cached || { datas: [], album: [] });
    const [load, setLoad] = useState(() => !cached || !cached.datas || cached.datas.length === 0);

    useEffect(() => {
        getHome(true).then((res) => {
            if (res && res.datas && res.datas.length > 0) {
                setDatas(res);
            }
            setLoad(false);
        });
    }, []);

    if (load || !data.datas || data.datas.length === 0) {
        return (
            <div className="min-h-[70vh] w-full flex items-center justify-center">
                <div className="w-12 h-12 rounded-full border-4 border-white/20 border-t-pink animate-spin" />
            </div>
        );
    }

    const services = SafeJsonParse(data.datas[4]?.value, []);

    return (
        <div className="flex flex-col w-full overflow-x-hidden relative">
            {/* ================= HERO SECTION ================= */}
            <section className="w-full min-h-[90vh] lg:min-h-screen relative flex items-center justify-center overflow-hidden pt-[110px] lg:pt-[80px] pb-10">
                {/* Hero Background */}
                <div className="absolute inset-0 z-0 overflow-hidden">
                    <motion.div
                        initial={{ scale: 1.1, opacity: 0 }}
                        animate={{ scale: 1, opacity: 0.35 }}
                        transition={{ duration: 1.4, ease: "linear" }}
                        className="w-full h-full bg-cover bg-bottom"
                        style={{ backgroundImage: "url('/images/bg.jpg')" }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 h-44 bg-gradient-to-t from-[#08090d] to-transparent" />
                </div>

                {/* Hero Content */}
                <div className="max-w-[1200px] w-full mx-auto relative z-10 text-center px-4">
                    <div className="flex flex-col items-center gap-6 md:gap-8">
                        {/* Brand Emblem (Zero Blur, Zero Shadow Animation, Linear Entrance) */}
                        <motion.div
                            initial={{ scale: 0.7, opacity: 0, y: -20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease: "linear" }}
                        >
                            <div className="w-[110px] sm:w-[130px] md:w-[150px] h-[110px] sm:h-[130px] md:h-[150px] rounded-full p-4 md:p-5 bg-white border-3 border-pink flex items-center justify-center anim-float">
                                <img
                                    src="/logo-crop.png"
                                    alt="I-FIT Logo"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                        </motion.div>

                        {/* Kinetic Typography with Linear Motion and Visible Durations */}
                        <div className="flex flex-col items-center gap-3 max-w-[900px]">
                            <motion.div
                                initial={{ opacity: 0, y: 25 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2, ease: "linear" }}
                            >
                                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-pink/15 border border-pink/40 mb-2">
                                    <div className="w-2 h-2 rounded-full bg-biruMuda animate-pulse" />
                                    <span className="text-xs md:text-sm font-bold text-biruMuda tracking-widest uppercase">
                                        The Premier Postural & Sports Health Center
                                    </span>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 1.0, delay: 0.35, ease: "linear" }}
                            >
                                <motion.h1
                                    className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-none bg-clip-text text-transparent uppercase select-none"
                                    style={{
                                        backgroundImage:
                                            "linear-gradient(90deg, #ffffff 0%, #ffffff 25%, #EA2A5E 45%, #ff4075 50%, #EA2A5E 55%, #ffffff 75%, #ffffff 100%)",
                                        backgroundSize: "250% 100%",
                                    }}
                                    animate={{
                                        backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                                    }}
                                    transition={{
                                        duration: 4.5,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                    }}
                                >
                                    {data.datas[0]?.value || "I-FIT INDONESIA"}
                                </motion.h1>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.55, ease: "linear" }}
                            >
                                <h2 className="text-sm sm:text-lg md:text-2xl font-semibold text-gray-300 tracking-widest uppercase mt-1">
                                    {data.datas[1]?.value || "POSTURAL SPECIALIST & FITNESS"}
                                </h2>
                            </motion.div>
                        </div>

                        {/* Hero CTA Button */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.7, ease: "linear" }}
                        >
                            <Link to="/contact">
                                <button
                                    type="button"
                                    className="px-8 py-3.5 rounded-full bg-gradient-to-r from-pink to-ungu text-white font-extrabold text-sm tracking-wider hover:scale-105 active:scale-95 transition-transform duration-200 flex items-center gap-2 cursor-pointer"
                                >
                                    <span>START ASSESSMENT</span>
                                    <HiArrowRight className="text-base" />
                                </button>
                            </Link>
                        </motion.div>

                        {/* Scroll Indicator */}
                        <motion.div
                            animate={{ y: [0, 8, 0] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "linear" }}
                            className="pt-4 flex flex-col items-center opacity-70 cursor-pointer"
                        >
                            <span className="text-[10px] font-bold tracking-widest text-pink uppercase">
                                SCROLL
                            </span>
                            <HiChevronDown className="text-lg text-pink mt-0.5" />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ================= ABOUT US SECTION ================= */}
            <section className="relative py-12 md:py-20 px-4 md:px-8">
                <IFHeading>ABOUT US</IFHeading>

                <div className="max-w-[1100px] mx-auto mt-6">
                    <div className="p-6 md:p-10 lg:p-12 rounded-3xl bg-[#12141e] border border-pink/25 relative overflow-hidden">
                        <div className="flex flex-col items-center text-center gap-8">
                            {/* Founder Portrait (Zero Blur, Linear Hover) */}
                            <motion.div
                                whileHover={{ scale: 1.04 }}
                                transition={{ duration: 0.3, ease: "linear" }}
                                className="relative w-36 sm:w-44 h-36 sm:h-44 rounded-full p-1 bg-gradient-to-tr from-pink to-biruMuda"
                            >
                                <div className="w-full h-full rounded-full bg-pink overflow-hidden relative">
                                    <img
                                        alt={data.datas[2]?.value || "Founder"}
                                        src="/images/kinan-crop.png"
                                        className="w-full h-full object-cover object-top"
                                    />
                                </div>
                            </motion.div>

                            {/* Credentials */}
                            <div className="flex flex-col items-center gap-2">
                                <span className="text-xs font-extrabold text-biruMuda uppercase tracking-widest">
                                    Founder & Lead Postural Specialist
                                </span>
                                <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-white tracking-tight">
                                    {data.datas[2]?.value}
                                </h3>
                            </div>

                            {/* Bio */}
                            <FadeIn delay={0.2} duration={0.9}>
                                <p className="text-sm md:text-base lg:text-lg leading-relaxed text-gray-300 whitespace-pre-line max-w-[850px] mx-auto">
                                    {data.datas[3]?.value}
                                </p>
                            </FadeIn>
                        </div>
                    </div>
                </div>
            </section>

            {/* ================= OUR SERVICES SECTION ================= */}
            <section className="relative py-12 md:py-20">
                <IFHeading>OUR SERVICES</IFHeading>

                <div className="max-w-[1300px] mx-auto px-4 md:px-8 mt-8">
                    <div className="flex flex-col gap-12 lg:gap-16 w-full">
                        {services.map((s, index) => {
                            const isEven = index % 2 === 0;
                            const serviceNumber = `0${index + 1}`;

                            return (
                                <div
                                    key={index}
                                    className="w-full p-6 md:p-8 lg:p-10 rounded-3xl bg-[#12141e] border border-white/10 hover:border-pink/40 transition-colors duration-200"
                                >
                                    <div
                                        className={`flex flex-col ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"
                                            } items-center gap-8 lg:gap-12 w-full`}
                                    >
                                        {/* Image Container */}
                                        <div className="w-full lg:w-[45%]">
                                            <FadeIn direction={isEven ? "right" : "left"} duration={0.9}>
                                                <div className="w-full aspect-[16/10] rounded-2xl overflow-hidden relative bg-black border border-white/10 group">
                                                    <img
                                                        alt={s.title}
                                                        src={process.env.REACT_APP_IMAGE_URL + s.image}
                                                        loading="lazy"
                                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                    />
                                                    {/* Service Badge (Zero Blur) */}
                                                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#08090d] border border-pink/40">
                                                        <span className="text-[11px] font-black text-pink uppercase tracking-wider">
                                                            SERVICE {serviceNumber}
                                                        </span>
                                                    </div>
                                                </div>
                                            </FadeIn>
                                        </div>

                                        {/* Text Info */}
                                        <div className="w-full lg:w-[55%]">
                                            <FadeIn direction={isEven ? "left" : "right"} duration={0.9}>
                                                <div className={`flex flex-col gap-4 ${isEven ? "items-start text-left" : "items-start lg:items-end lg:text-right"}`}>
                                                    <IFSubMenu
                                                        textAlign={isEven ? "left" : "right"}
                                                        text1={s.title}
                                                        text2={s.subTitle}
                                                    />

                                                    <p className="text-xs sm:text-sm md:text-base leading-relaxed text-gray-300 whitespace-pre-line">
                                                        {s.description}
                                                    </p>

                                                    <Link to="/contact">
                                                        <button
                                                            type="button"
                                                            className="mt-2 px-5 py-2 rounded-full bg-pink/15 border border-pink text-pink hover:bg-pink hover:text-white transition-colors duration-200 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 cursor-pointer"
                                                        >
                                                            <span>Consult Now</span>
                                                            <HiArrowRight className="text-sm" />
                                                        </button>
                                                </Link>
                                                </div>
                                            </FadeIn>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ================= ALBUM SWIPER ================= */}
            <IFSwiper data={data.album} />
        </div>
    );
}
